import { haversineDistanceM } from './mission-planning';
import type {
  TimelineBlock, TimelineLane, CoverageTimelineView,
} from '@/types/coverage-timeline';

// ---------------------------------------------------------------------------
// SHOVRON continuous coverage - the ATTACH-TIME projection (PR 3, S2).
//
// What a rotation would do, before anything is attached, so the operator can answer "how many
// aircraft do I need for this window" before the commit rather than by attaching, waiting, and
// reading the achieved percentage afterwards.
//
// PURE. No clock, no fetch, no React. `now` is an argument, exactly as `buildCoverageTimeline` and
// `computeReplacementTiming` take theirs - which is what makes the whole refusal matrix testable
// without a fleet, and what will make this module deletable in one move when
// `POST /api/coverage/preview` replaces it with the server's own planner.
//
// IT IS AN ESTIMATE AND EVERY SURFACE MUST SAY SO. The server planner knows observed drain,
// calibrated cruise speed, bay hold and dock contention. This knows the first two (fed to it from
// `GET /api/drones/energy`) and neither of the last two. The divergence is the risk the whole
// client-first decision accepted, and the mitigation is three-part: real resolutions rather than
// invented ones, the SHARED turnaround constants below, and the label on the surface.
//
// WHAT IT WILL NOT DO. It will not size an aircraft this platform cannot size. An unknown
// endurance, an unknown cruise speed or no dock produces a NAMED REFUSAL and that aircraft is left
// out of the rotation - never a plausible default. `airframe-endurance.ts` is written around the
// same rule and states why: a substituted default is indistinguishable from a measurement, and
// #177 is what breaking it looked like in production. Leaving an aircraft out UNDERSTATES the
// coverage its set would achieve, which is the safe direction; inventing a number for it
// overstates coverage, which is the direction that leaves a station unmanned.
// ---------------------------------------------------------------------------

/**
 * The charge-rate half of the turnaround model, MIRRORED from `src/services/dock-time-to-ready.ts`.
 *
 * This is not this module's number to choose. It was centralised in that file in stage 5 W1
 * precisely so "the picture the operator reads and the number the planner decides on cannot drift
 * apart" - the preview and the chart drawn after attach must not disagree about the same
 * turnaround. If it changes there, it changes here.
 *
 * (The UX review that specified this feature modelled 8 minutes for a swap and 55 for charging.
 * Both are wrong for this platform: AirScort measured a swap at 4-5 minutes, and charging from a
 * 20% landing floor to a full pack is about 36. The shared model wins.)
 *
 * THE SWAP HALF IS NO LONGER A CONSTANT. It is the org's `settings.dock.swap_duration_s`, read by
 * `useSwapDuration` and passed into `projectCoverage` - a hardcoded mirror of it here was the
 * second copy of a number this module's own header warns must not drift. See `@/lib/dockSwap`.
 */
export const CHARGE_RATE_PCT_PER_MIN = 2.2;

/**
 * The planner's safety margin, MIRRORED from `DEFAULT_MARGIN_FRACTION` in
 * `src/services/replacement-timing.ts`.
 *
 * The rotation that will actually fly does not spend the whole budget: `computeReplacementTiming`
 * takes a fifth off it once, on the whole thing, and `coverage-planner.ts` never overrides the
 * default. Leaving it out here made the preview promise 25% more station time per sortie than the
 * platform will deliver - two aircraft on charging docks came back as 100% of an open-ended window
 * where the planner leaves a five-and-a-half-minute hole in every forty. That is the fail-open
 * direction, in the surface whose entire purpose is answering "how many aircraft do I need".
 *
 * Mirrored rather than fetched for the same reason as the turnaround constants: one number, so the
 * picture the operator reads before the attach and the schedule the planner runs after it cannot
 * disagree.
 */
export const SAFETY_MARGIN_FRACTION = 0.2;

/**
 * The battery the platform assumes a DJI airframe's own low-battery RTH fires at, MIRRORED from
 * `DJI_LOW_BATTERY_RTH_ASSUMED_PCT` in `src/lib/dji-rth-policy.ts` (#703).
 *
 * Mirrored rather than imported for the reason every constant in this block is: the client must
 * not read server source (a client test that did exactly that has broken `main` invisibly before,
 * because a server-only PR can move a constant and skip the client job).
 *
 * WHY THE PROJECTION NEEDS IT AT ALL. Since #703 the planner does not budget against
 * `battery_normal_pct` as typed - it takes the higher of that and the percentage at which this
 * airframe's own RTH would take over, because once that fires it holds the flight controller and
 * the sortie ends on a GPS-home landing. The wizard's default is 20 and nothing in the field
 * reports a real threshold yet, so TODAY every default attach is raised to this figure. A
 * projection that spent 80% of the pack would size the fleet against a plan the server will not
 * fly - optimistic, which is the direction that leaves a station unmanned, and the direction this
 * file's own header says none of its caveats may be.
 */
export const DJI_LOW_BATTERY_RTH_ASSUMED_PCT = 25;

/**
 * The gap limit a rotation is GRADED against, MIRRORED from `MAX_ACCEPTABLE_GAP_SECONDS` in
 * `src/services/coverage-achieved.ts`.
 *
 * COVERAGE IS JUDGED ON TWO CRITERIA, NOT ONE, and the second is the one that catches the
 * dangerous shape. `CoverageRibbon` takes its PASS/FAIL from `meets_target && meets_max_gap_
 * criterion` and its own comment says why: 99% of window against a 95% target, made of one
 * four-minute hole, passes the percentage and fails this outright. A simulator that graded on the
 * percentage alone would put a green MEETS over a rotation the status board grades FAIL an hour
 * later - two answers about one plan, which is the opposite of one picture used twice.
 */
export const MAX_ACCEPTABLE_GAP_SECONDS = 60;

/** A turnaround charges to a FULL pack, not to the launch floor - `coverage-timeline.ts`'s target. */
const FULL_CHARGE_TARGET_PCT = 100;

/**
 * Dock latency from "launch it" to "airborne", seconds.
 *
 * Mirrors `BAY_MOVE_TIMEOUT_MS` in `airscort-dock-adapter.ts`, which is the fallback
 * `replacement-timing.ts` itself uses when a dock has no measured latency of its own. Counted in
 * WALL CLOCK but not against the airframe's battery: the pack is not being drawn on while the bay
 * opens. Omitting it entirely would make every relief arrive earlier in the preview than in
 * reality - the fail-open direction.
 */
export const BAY_LAUNCH_LATENCY_SECONDS = 60;

/**
 * How far ahead an UNTIL STOPPED watch is planned.
 *
 * An open-ended rotation has no end to measure against, so the preview plans a rolling horizon and
 * says so. Eight hours is the review's figure and is roughly one shift.
 */
export const ROLLING_HORIZON_SECONDS = 8 * 60 * 60;

/** The furthest this will project even for a window that asks for more. */
export const MAX_HORIZON_SECONDS = 12 * 60 * 60;

/**
 * A stop on the simulation, not a modelling choice.
 *
 * Every leg advances the cursor by at least one dwell, and a dwell is refused below zero, so this
 * cannot be reached by any input that passes the per-aircraft checks. It is here because a
 * projection that runs in a browser render loop must have a bound that does not depend on the
 * arithmetic above it being right.
 */
const MAX_LEGS = 400;

/** Why an aircraft could not be projected. Each is a sentence a surface can show as-is. */
export type ProjectionRefusal =
  /** No calibrated profile and no nominal figure for its model. */
  | 'endurance_unknown'
  /** No calibrated cruise speed and no planned speed on the mission. */
  | 'cruise_speed_unknown'
  /** Stationed in no dock, or in a dock with no recorded position. */
  | 'no_dock_position'
  /** It cannot reach the station and return on its own reserve, so it has no dwell at all. */
  | 'cannot_reach_station';

export interface ProjectionAircraft {
  readonly droneId: string;
  readonly callsign: string | null;
  readonly name: string;
  /** Usable endurance at a full pack, minutes. `null` means this platform cannot size it. */
  readonly enduranceMinutesAtFull: number | null;
  /** Metres per second. `null` means unknown - never a substituted figure. */
  readonly cruiseSpeedMs: number | null;
  /** Its home bay. `null` for an aircraft stationed nowhere, which has nothing to return to. */
  readonly dock: {
    readonly name: string | null;
    readonly lat: number | null;
    readonly lon: number | null;
    /** `capabilities` contains `battery_switch` - the one definition of a swap dock. */
    readonly isSwap: boolean;
  } | null;
}

export interface ProjectionInput {
  readonly now: Date;
  readonly missionId: string;
  readonly station: { readonly lat: number; readonly lon: number };
  /** Both `null` is UNTIL STOPPED, which plans against `ROLLING_HORIZON_SECONDS`. */
  readonly windowStart: Date | null;
  readonly windowEnd: Date | null;
  /** The charge an aircraft lands at. Bounds both the usable endurance and the charge-back. */
  readonly batteryNormalPct: number;
  /** IN ROTATION ORDER. The order is the operator's and this honours it - see `nextInOrder`. */
  readonly members: readonly ProjectionAircraft[];
  /**
   * The org's `settings.dock.swap_duration_s`, from `useSwapDuration`.
   *
   * An argument rather than a read, because this module is pure by contract - it runs on every
   * keystroke inside a `useMemo` and is unit-tested against fixed inputs. A fetch inside it would
   * make the width of a swap block depend on I/O no test can see.
   */
  readonly swapDurationSeconds: number;
}

export interface ProjectionGap {
  readonly startsAt: string;
  readonly endsAt: string;
  readonly seconds: number;
}

/** One aircraft's derived numbers, or the reason it has none. Exported for the surface to show. */
export interface AircraftPlan {
  readonly droneId: string;
  readonly callsign: string | null;
  readonly name: string;
  /**
   * The bay this aircraft returns to, carried through because the machine CLASS is the fact that
   * decides a rotation and a duration alone does not say it.
   *
   * "5m 00s turnaround" and "36m 22s turnaround" are a swap magazine and a charging dock, and an
   * operator should not have to decode which from the arithmetic. Null when it has no dock, which
   * is itself a refusal.
   */
  readonly dockName: string | null;
  readonly isSwapDock: boolean | null;
  /** Usable endurance at a full pack, as resolved - the input, beside the figures derived from it. */
  readonly enduranceMinutesAtFull: number | null;
  readonly refusal: ProjectionRefusal | null;
  /** Flight time from its dock to the station, one way. `null` when refused. */
  readonly transitSeconds: number | null;
  /** Bay latency plus that transit - the wall clock from "launch it" to "on station". */
  readonly launchToStationSeconds: number | null;
  /** How long it can hold the station on one pack, after both transits. `null` when refused. */
  readonly dwellSeconds: number | null;
  /** How long it sits in its dock before it can fly again. `null` when refused. */
  readonly turnaroundSeconds: number | null;
}

/**
 * Why nothing could be projected, when nothing could.
 *
 * A bare `scorable: false` is not something a surface can act on: "no aircraft could be sized",
 * "the battery policy is not a usable pair of numbers" and "the window ends before it starts" send
 * the operator to three different fields. Every one of these is reachable from a half-typed form,
 * because the panel's own blockers gate the PRESS and not the preview - `min`/`max` on an input do
 * not constrain a typed value, which `attachBlockers` says in as many words.
 */
export type UnscorableReason =
  /**
   * Nothing has been picked yet - the wizard's OPENING state, not a fault.
   *
   * Separate from `no_projectable_aircraft` because they send the operator to opposite places: one
   * is "click the Aircraft tab", the other is a calibration flight this platform has never run. A
   * shared sentence put the expensive remedy under an amber warning triangle on the default tab
   * before the operator had done anything at all.
   */
  | 'no_aircraft_picked'
  /** Aircraft WERE picked and every one was refused. Their individual reasons are in `plans`. */
  | 'no_projectable_aircraft'
  /** The mission has no usable hold point to measure a transit from. */
  | 'station_position_invalid'
  /** `batteryNormalPct` is not a percentage in (0, 100] - an empty field reads as 0. */
  | 'battery_policy_invalid'
  /** A window bound could not be read as a date. */
  | 'window_invalid'
  /** The window ends at or before it starts, so there is no time to score. */
  | 'window_empty';

export interface ProjectionVerdict {
  /**
   * Could the window be scored AT ALL?
   *
   * False when no picked aircraft could be projected, or when an input this cannot work from was
   * typed. The surface must then say the window cannot be scored - NOT 0%, which is a measurement
   * and would read as "this rotation covers nothing".
   */
  readonly scorable: boolean;
  /** Set exactly when `scorable` is false. */
  readonly unscorableReason: UnscorableReason | null;
  /** Percent of the projected window the station is held. `null` when not scorable. */
  readonly coveredPct: number | null;
  readonly longestGapSeconds: number | null;
  readonly gaps: readonly ProjectionGap[];
  /** The horizon actually projected over, which an UNTIL STOPPED watch does not choose. */
  readonly windowStartsAt: string;
  readonly windowEndsAt: string;
  /** True when the window was cut short by `MAX_HORIZON_SECONDS` or the rolling horizon. */
  readonly horizonTruncated: boolean;
  /** Every aircraft, in rotation order, with its derived numbers or its refusal. */
  readonly plans: readonly AircraftPlan[];
  /**
   * Aircraft left OUT of the rotation because they could not be sized.
   *
   * The figure above is therefore what the REMAINING aircraft achieve, which understates the set
   * the operator picked. A surface must say so - a percentage that silently describes a different
   * rotation than the one on screen is worse than no percentage.
   */
  readonly refusedCount: number;
  /**
   * Every aircraft is assumed ready to launch when the window opens.
   *
   * The one optimism in the model, and it is named rather than hidden: a dock mid-charge or an
   * airframe in maintenance will not in fact be ready. Step 2 shows each aircraft's live status
   * beside it, which is where that is visible; this flag is so the preview can say that the
   * figure does not account for it.
   */
  readonly assumesAllReadyAtStart: boolean;
}

export interface ProjectionResult {
  readonly view: CoverageTimelineView;
  readonly verdict: ProjectionVerdict;
}

/**
 * A plan with every number present, which is exactly what `refusal === null` means.
 *
 * A type guard rather than four non-null assertions in the loop below. The assertions would be
 * correct today and would survive, silently, a later edit that let a refusal through with a
 * missing figure - and the failure mode of that is arithmetic on `undefined`, which is `NaN`, a
 * block with no width, and a window that scores as fully covered.
 */
interface ResolvedPlan extends AircraftPlan {
  readonly refusal: null;
  readonly transitSeconds: number;
  readonly launchToStationSeconds: number;
  readonly dwellSeconds: number;
  readonly turnaroundSeconds: number;
}

function isResolved(plan: AircraftPlan): plan is ResolvedPlan {
  // `Number.isFinite`, not `!== null`. The failure this guard exists to stop is arithmetic on an
  // absent value, and that arithmetic produces `NaN` rather than `null` - a plan carrying a NaN
  // dwell passes every null check, reaches `new Date(NaN).toISOString()` and throws.
  return plan.refusal === null
    && Number.isFinite(plan.transitSeconds)
    && Number.isFinite(plan.launchToStationSeconds)
    && Number.isFinite(plan.dwellSeconds)
    && Number.isFinite(plan.turnaroundSeconds);
}

/** The same arithmetic as `turnaroundSeconds` in `dock-time-to-ready.ts`, and it must stay so. */
export function turnaroundSecondsFor(
  isSwapDock: boolean,
  landsAtPct: number,
  swapDurationSeconds: number,
): number {
  if (isSwapDock) return swapDurationSeconds;
  const deficit = Math.max(0, FULL_CHARGE_TARGET_PCT - landsAtPct);
  return (deficit / CHARGE_RATE_PCT_PER_MIN) * 60;
}

function finite(v: number | null | undefined): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/**
 * One aircraft's numbers, or the first reason it has none.
 *
 * ORDER MATTERS in the refusal chain: it reports the most fundamental missing input rather than
 * the last one checked, so an aircraft with neither endurance nor a dock is reported as the
 * endurance problem - the one a calibration flight fixes.
 */
export function planAircraft(
  aircraft: ProjectionAircraft,
  station: { lat: number; lon: number },
  batteryNormalPct: number,
  swapDurationSeconds: number,
): AircraftPlan {
  const endurance = finite(aircraft.enduranceMinutesAtFull);
  const base = {
    droneId: aircraft.droneId,
    callsign: aircraft.callsign,
    name: aircraft.name,
    dockName: aircraft.dock?.name ?? null,
    isSwapDock: aircraft.dock?.isSwap ?? null,
    // Carried even on a refusal: an aircraft refused for its DOCK still has a known endurance, and
    // a surface that blanked it would say "unknown" about a figure the platform holds.
    enduranceMinutesAtFull: endurance,
    transitSeconds: null,
    launchToStationSeconds: null,
    dwellSeconds: null,
    turnaroundSeconds: null,
  };

  if (endurance === null || endurance <= 0) return { ...base, refusal: 'endurance_unknown' };

  const cruise = finite(aircraft.cruiseSpeedMs);
  if (cruise === null || cruise <= 0) return { ...base, refusal: 'cruise_speed_unknown' };

  const dockLat = finite(aircraft.dock?.lat);
  const dockLon = finite(aircraft.dock?.lon);
  if (aircraft.dock === null || dockLat === null || dockLon === null) {
    // A rotation member with nowhere to be resupplied holds the station until its reserve is gone
    // and then has nowhere to go. The attach form already refuses this for a rotation of ONE; the
    // projection refuses it for any member, because a lane with no dock has no cycle to draw.
    return { ...base, refusal: 'no_dock_position' };
  }

  const distanceM = haversineDistanceM({ lat: dockLat, lng: dockLon }, { lat: station.lat, lng: station.lon });
  const transitSeconds = distanceM / cruise;

  // USABLE PACK, NOT THE WHOLE PACK. The aircraft lands at `battery_normal_pct`, so what it can
  // spend is the endurance above that floor. Deriving it from the operator's own battery policy
  // rather than a flat reserve is also what makes step 3 redraw the chart.
  //
  // Then the planner's margin, on the whole budget, once - the same shape as
  // `replacement-timing.ts`'s `rawBudget - rawBudget * marginFraction`. Both transits come off
  // first because this aircraft starts in its dock with a full pack, where the server's version
  // starts from an airframe already on station and so subtracts only the return.
  // THE FIGURE THE SERVER WILL ACTUALLY BUDGET AGAINST, not the one the operator typed (#703).
  // `resolveEffectiveReservePct` takes the higher of the two, so projecting against the typed
  // number alone overstates every sortie by the difference - 5 points on the wizard's own default
  // of 20, which is roughly two minutes of station time per aircraft per cycle.
  const effectiveReservePct = Math.max(batteryNormalPct, DJI_LOW_BATTERY_RTH_ASSUMED_PCT);
  const usableSeconds = endurance * 60 * ((100 - effectiveReservePct) / 100);
  const rawBudgetSeconds = usableSeconds - 2 * transitSeconds;
  const dwellSeconds = rawBudgetSeconds * (1 - SAFETY_MARGIN_FRACTION);
  if (!(dwellSeconds > 0)) {
    return {
      ...base,
      refusal: 'cannot_reach_station',
      transitSeconds,
      launchToStationSeconds: BAY_LAUNCH_LATENCY_SECONDS + transitSeconds,
      turnaroundSeconds: turnaroundSecondsFor(aircraft.dock.isSwap, batteryNormalPct, swapDurationSeconds),
    };
  }

  return {
    ...base,
    refusal: null,
    transitSeconds,
    launchToStationSeconds: BAY_LAUNCH_LATENCY_SECONDS + transitSeconds,
    dwellSeconds,
    turnaroundSeconds: turnaroundSecondsFor(aircraft.dock.isSwap, batteryNormalPct, swapDurationSeconds),
  };
}

/** A `Date` that can be read as one. `new Date('half-typed')` is an object, and `NaN` inside. */
function dateMs(d: Date | null): number | null {
  if (d === null) return null;
  const ms = d.getTime();
  return Number.isFinite(ms) ? ms : null;
}

/**
 * The window this projection actually runs over, and whether it was cut short.
 *
 * `invalid` is not the same as an absent bound: a `datetime-local` box mid-edit produces a Date
 * that is present and unreadable, and treating that as "UNTIL STOPPED" would answer a question
 * the operator has not finished asking.
 */
export function resolveHorizon(input: {
  now: Date;
  windowStart: Date | null;
  windowEnd: Date | null;
}): { startMs: number; endMs: number; truncated: boolean; invalid: boolean } {
  const nowMs = dateMs(input.now);
  const startBound = dateMs(input.windowStart);
  const endBound = dateMs(input.windowEnd);
  if (nowMs === null
    || (input.windowStart !== null && startBound === null)
    || (input.windowEnd !== null && endBound === null)) {
    return { startMs: 0, endMs: 0, truncated: false, invalid: true };
  }

  const requestedStart = startBound ?? nowMs;
  // A window that has already opened is planned from NOW, not from its start: the minutes before
  // the operator is standing here are not a plan, and scoring them would be the same defect the
  // achieved figure had before PR 2 clamped it to the row's own `created_at`.
  const startMs = Math.max(nowMs, requestedStart);

  const cap = startMs + MAX_HORIZON_SECONDS * 1000;
  const rolling = startMs + ROLLING_HORIZON_SECONDS * 1000;
  const requestedEnd = endBound ?? rolling;
  const endMs = Math.min(requestedEnd, cap);

  return {
    startMs,
    endMs,
    truncated: input.windowEnd === null || requestedEnd > cap,
    invalid: false,
  };
}

/**
 * A result that draws nothing and claims nothing, for an input this cannot work from.
 *
 * The lanes are still returned, one per member with no bars, so the chart keeps the shape of the
 * rotation the operator has picked while the figure beside it says what is wrong. An empty view
 * would make a half-typed date look like a rendering fault.
 */
function unscorable(
  input: ProjectionInput,
  plans: readonly AircraftPlan[],
  reason: UnscorableReason,
): ProjectionResult {
  const nowIso = Number.isFinite(input.now.getTime())
    ? input.now.toISOString()
    // Only reachable with an unreadable `now`, which is itself the caller's bug - but this
    // function's whole contract is that it does not throw, and `toISOString` would.
    : new Date(0).toISOString();
  return {
    view: {
      missionId: input.missionId,
      now: nowIso,
      horizonEndsAt: nowIso,
      lanes: input.members.map((m, i) => ({
        droneId: m.droneId,
        callsign: m.callsign,
        rotationOrder: i + 1,
        isActive: false,
        dockName: m.dock?.name ?? null,
        blocks: [],
      })),
      forecastAvailable: false,
      singleAircraftRotation: input.members.length === 1,
      selfSwapCycle: false,
      selfSwapGapSeconds: null,
    },
    verdict: {
      scorable: false,
      unscorableReason: reason,
      coveredPct: null,
      longestGapSeconds: null,
      gaps: [],
      windowStartsAt: nowIso,
      windowEndsAt: nowIso,
      horizonTruncated: false,
      plans,
      refusedCount: plans.filter((p) => p.refusal !== null).length,
      assumesAllReadyAtStart: true,
    },
  };
}

/**
 * Project the rotation this configuration would fly.
 *
 * ONE LOOP FOR BOTH SHAPES, unlike the server's two branches. `coverage-timeline.ts` needs a
 * separate `projectSelfSwapCycle` because it starts mid-cycle from a live phase and a rotation of
 * one has no reliever/outgoing pair to walk. From a standing start there is no such distinction:
 * an aircraft takes the station when it is ready and the station is uncovered until it is, and a
 * rotation of one is simply the case where the next aircraft in order is the one that just left.
 * The self-swap gap falls out of the same arithmetic rather than being a special case.
 *
 * IT NEVER THROWS. It runs on every keystroke of a form whose fields are not constrained until the
 * press, in an app with no error boundary - `MissionCoverageTimeline` carries the same rule and
 * the same reason: one `RangeError` out of a render takes the whole mission page down. Every
 * input that cannot be worked from comes back as `scorable: false` with a reason naming the field.
 */
export function projectCoverage(input: ProjectionInput): ProjectionResult {
  const horizon = resolveHorizon(input);
  const { startMs, endMs, truncated } = horizon;
  const plans = input.members.map(
    (m) => planAircraft(m, input.station, input.batteryNormalPct, input.swapDurationSeconds),
  );

  /**
   * The three inputs the FORM drives, checked before any arithmetic touches them.
   *
   * The per-aircraft inputs are guarded inside `planAircraft`; these three are not per aircraft and
   * were the ones that could reach `new Date(NaN)`. An empty percentage field reads as `0`, and a
   * half-typed window bound is a `Date` that is present and unreadable.
   */
  const stationOk = Number.isFinite(input.station.lat) && Number.isFinite(input.station.lon);
  const batteryOk = Number.isFinite(input.batteryNormalPct)
    && input.batteryNormalPct > 0
    && input.batteryNormalPct <= 100;

  const inputProblem: UnscorableReason | null = horizon.invalid
    ? 'window_invalid'
    : !stationOk
      ? 'station_position_invalid'
      : !batteryOk
        ? 'battery_policy_invalid'
        : null;

  if (inputProblem !== null) {
    // NO PER-AIRCRAFT VERDICTS FROM A BROKEN FORM. `plans` above was computed against the station
    // and the battery policy, so when either is unusable every entry carries a `cannot_reach_
    // station` refusal produced by arithmetic on a NaN - and step 2 renders that as a claim about
    // those airframes' endurance, two lines under the sentence correctly blaming the form. A
    // whole-form problem must never manufacture an airworthiness refusal.
    return unscorable(input, [], inputProblem);
  }

  const lanes = new Map<string, TimelineBlock[]>();
  for (const m of input.members) lanes.set(m.droneId, []);

  /** Only aircraft with a full set of numbers fly. The rest are named, not guessed at. */
  const eligible = input.members
    .map((m, i) => ({ member: m, plan: plans[i] as AircraftPlan, readyAt: startMs }))
    .filter((e): e is { member: ProjectionAircraft; plan: ResolvedPlan; readyAt: number } =>
      isResolved(e.plan));

  const gaps: ProjectionGap[] = [];
  const reached = new Set<string>();
  const spanMs = Math.max(0, endMs - startMs);

  // Two more ways there is nothing to score, and they are different sentences: a window that ends
  // before it starts is a field to fix, and a rotation of unsizable aircraft is a calibration
  // flight. Collapsing them into one bare "cannot be scored" sends the operator to the wrong one.
  if (spanMs <= 0) return unscorable(input, plans, 'window_empty');
  if (eligible.length === 0) {
    return unscorable(
      input,
      plans,
      plans.length === 0 ? 'no_aircraft_picked' : 'no_projectable_aircraft',
    );
  }

  {
    /**
     * Was this window SCHEDULED for later, or does it start now?
     *
     * The whole difference is whether the first aircraft could already be on station when the plan
     * begins. `resolveHorizon` clamps a start that has passed up to `now`, so anything that is not
     * strictly in the future is a watch beginning at this moment with the aircraft in its dock.
     */
    const startsScheduled = startMs > input.now.getTime();
    let cursor = startMs;
    let order = 0;

    for (let leg = 0; leg < MAX_LEGS && cursor < endMs; leg += 1) {
      const slot = eligible[order % eligible.length] as typeof eligible[number];
      const {
        launchToStationSeconds: launchToStation,
        dwellSeconds: dwell,
        transitSeconds: transit,
        turnaroundSeconds: turnaround,
      } = slot.plan;

      // IS THE FIRST AIRCRAFT ALREADY UP WHEN THE WINDOW OPENS? Only if the window opens LATER.
      //
      // For a watch scheduled for tomorrow morning the operator launches into it, and drawing the
      // climb-out to the left of the chart's own start would be a bar nobody can see. But UNTIL
      // STOPPED and every "next N hours" preset start NOW, with the aircraft in its dock - and
      // treating it as already on station counted the bay latency and the whole climb-out as
      // covered. That is a head-of-window hole scored as coverage, on the commonest configuration
      // there is: a well-configured two-ship rotation answered "100.0%, longest gap none" for a
      // plan whose station is empty for the first several minutes. Overstating coverage is the
      // direction that leaves a station unmanned.
      const onStationAt = leg === 0
        ? (startsScheduled ? cursor : cursor + launchToStation * 1000)
        : Math.max(cursor, slot.readyAt + launchToStation * 1000);

      if (onStationAt > cursor) {
        const gapEnds = Math.min(onStationAt, endMs);
        gaps.push({
          startsAt: new Date(cursor).toISOString(),
          endsAt: new Date(gapEnds).toISOString(),
          seconds: (gapEnds - cursor) / 1000,
        });
      }
      if (onStationAt >= endMs) break;

      const lane = lanes.get(slot.member.droneId) ?? [];
      // The climb-out is drawn whenever it happens INSIDE the window - which for a watch starting
      // now includes the first one. Only a scheduled window has a first leg flown before the plan.
      if (leg > 0 || !startsScheduled) {
        lane.push({
          type: 'transit',
          startsAt: new Date(onStationAt - launchToStation * 1000).toISOString(),
          endsAt: new Date(onStationAt).toISOString(),
          source: 'forecast',
        });
      }

      const leavesAt = onStationAt + dwell * 1000;
      lane.push({
        type: 'on_station',
        startsAt: new Date(onStationAt).toISOString(),
        endsAt: new Date(Math.min(leavesAt, endMs)).toISOString(),
        source: 'forecast',
      });
      reached.add(slot.member.droneId);

      // The legs home are drawn only when the aircraft actually flies them inside the horizon.
      // Drawing a return past the end of the window would put a bar where the chart has no axis.
      if (leavesAt < endMs) {
        const homeAt = leavesAt + transit * 1000;
        lane.push({
          type: 'transit',
          startsAt: new Date(leavesAt).toISOString(),
          endsAt: new Date(Math.min(homeAt, endMs)).toISOString(),
          source: 'forecast',
        });
        if (homeAt < endMs) {
          lane.push({
            type: slot.member.dock?.isSwap ? 'swap' : 'charging',
            startsAt: new Date(homeAt).toISOString(),
            endsAt: new Date(Math.min(homeAt + turnaround * 1000, endMs)).toISOString(),
            source: 'forecast',
          });
        }
      }

      slot.readyAt = leavesAt + (transit + turnaround) * 1000;
      cursor = leavesAt;
      order += 1;
    }
  }

  // Every eligible aircraft that never took a turn inside the horizon is queued, plainly - the
  // same STANDBY the server draws. A REFUSED aircraft deliberately gets no bar at all: a standby
  // bar says "parked, waiting its turn", and this one is never going to get a turn.
  for (const e of eligible) {
    if (!reached.has(e.member.droneId)) {
      lanes.get(e.member.droneId)?.push({
        type: 'standby',
        startsAt: new Date(startMs).toISOString(),
        endsAt: null,
        source: 'forecast',
      });
    }
  }

  const uncoveredSeconds = gaps.reduce((sum, g) => sum + g.seconds, 0);
  const soloPlan: ResolvedPlan | null = eligible.length === 1 ? eligible[0].plan : null;

  const timelineLanes: TimelineLane[] = input.members.map((m, i) => ({
    droneId: m.droneId,
    callsign: m.callsign,
    rotationOrder: i + 1,
    // A REFUSED aircraft is not an active lane. The chart draws an inactive one as stood down,
    // which is exactly what it is - a member that will not be taking a turn. Marking it active
    // with no bars is indistinguishable from a rendering fault.
    isActive: eligible.some((e) => e.member.droneId === m.droneId),
    dockName: m.dock?.name ?? null,
    blocks: (lanes.get(m.droneId) ?? []).sort(
      (a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime(),
    ),
  }));

  return {
    view: {
      missionId: input.missionId,
      // The chart's NOW is where the PLAN starts, which for a window opening later today is that
      // moment rather than this one. A preview has no history, so there is nothing to the left of
      // it that a true "now" would separate.
      now: new Date(startMs).toISOString(),
      horizonEndsAt: new Date(endMs).toISOString(),
      lanes: timelineLanes,
      forecastAvailable: true,
      // ONE ACTIVE MEMBER, matching what the shared type documents and what the server derives it
      // from. Counting every picked aircraft would say "2 aircraft" over a chart on which only one
      // of them ever flies.
      singleAircraftRotation: eligible.length === 1,
      // A cycle is DRAWN only if the aircraft actually leaves and comes back inside the horizon,
      // which is what a gap on the chart means. PR 2's rule: nothing may narrate a gap that is not
      // on the picture.
      selfSwapCycle: soloPlan !== null && gaps.length > 0,
      selfSwapGapSeconds: soloPlan === null
        ? null
        : soloPlan.transitSeconds + soloPlan.turnaroundSeconds + soloPlan.launchToStationSeconds,
    },
    verdict: {
      scorable: true,
      unscorableReason: null,
      coveredPct: ((spanMs / 1000 - uncoveredSeconds) / (spanMs / 1000)) * 100,
      longestGapSeconds: gaps.length === 0 ? 0 : Math.max(...gaps.map((g) => g.seconds)),
      gaps,
      windowStartsAt: new Date(startMs).toISOString(),
      windowEndsAt: new Date(endMs).toISOString(),
      horizonTruncated: truncated,
      plans,
      refusedCount: plans.filter((p) => p.refusal !== null).length,
      assumesAllReadyAtStart: true,
    },
  };
}
